import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Select } from 'antd';
import styled from 'styled-components';
import { Drawer } from '../../common/Drawer';
import CustomButton from '../../common/Button';
import {
  FooterRow,
  StyledCol,
  StyledFormItem,
} from '../Facility/FacilityFormModal';
import { UserRole } from '../../../enums/enums';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: UserFormType) => void;
  initialValues?: Partial<UserFormType & { roleId: number }> | null;
}

interface BasicUser {
  userId?: number;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  userRole?: number;
  entityStatus?: number;
  roleId?: number;
}

interface ExtendedUser extends BasicUser {
  createdOn?: string;
  profilePictureUrl?: string;
  bio?: string;
  role?: number;
}

interface StyledOptionProps {
  $isSelectable: boolean;
}

type NormalizableUser = BasicUser | ExtendedUser;

const status: Record<string, number> = { Active: 1, Inactive: 0 };

interface UserFormType {
  fullName: string;
  email: string;
  phoneNumber: string;
  roleId: number;
  userRole?: number;
}

const { Option } = Select;

const UserForm: React.FC<UserFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues,
}) => {
  const [form] = Form.useForm<UserFormType>();
  const formRef = useRef<HTMLDivElement>(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState<boolean>(false);
  const isEditMode = !!initialValues;
  const isRestrictedRole =
    initialValues?.userRole === UserRole.FacilityOwner ||
    initialValues?.userRole === UserRole.EventOrganizer;
  const isFormReadOnly = isEditMode && isRestrictedRole;

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        onSubmit(values);
      })
      .catch(() => {
        console.log('error');
      });
  }, [form, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
    if (formRef.current) {
      formRef.current.scrollTop = 0;
    }
  }, [form, onClose]);

  const normalizeValues = (values: NormalizableUser | null | undefined) => {
    if (!values) return {};
    return {
      fullName: values.fullName || '',
      email: values.email || '',
      phoneNumber: values.phoneNumber || '',
      userRole: values.userRole,
      entityStatus: values.entityStatus,
    };
  };

  const initialCleanValues = normalizeValues(initialValues);

  const handleFormChange = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some(({ errors }) => errors.length > 0);

    const currentValues = normalizeValues(form.getFieldsValue());

    let hasChanges = false;
    if (initialValues) {
      hasChanges = Object.keys(currentValues).some((key) => {
        const initialValue =
          initialCleanValues[key as keyof typeof initialCleanValues];
        const currentValue = currentValues[key as keyof typeof currentValues];
        return initialValue !== currentValue;
      });
    } else {
      hasChanges = form.isFieldsTouched(true);
    }
    setIsButtonEnabled(!hasErrors && hasChanges);
  }, [form, initialCleanValues, initialValues]);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue(initialValues as UserFormType);
      } else {
        form.resetFields();
      }
    }
  }, [open, initialValues, form]);

  return (
    <Drawer
      title={initialValues ? 'Edit User' : 'Add New User'}
      open={open}
      onClose={handleClose}
      width="40vw"
      footer={
        <FooterRow key="form-actions">
          <CustomButton key="cancel" onClick={handleClose}>
            Cancel
          </CustomButton>
          <CustomButton
            key="submit"
            type="primary"
            onClick={handleSubmit}
            disabled={!isButtonEnabled || isFormReadOnly}
          >
            {initialValues ? 'Edit' : 'Create'} User
          </CustomButton>
        </FooterRow>
      }
    >
      <StyledCol ref={formRef}>
        <Form form={form} layout="vertical" onFieldsChange={handleFormChange}>
          <StyledFormItem
            name="fullName"
            label="Full Name"
            // eslint-disable-next-line quotes
            rules={[{ required: true, message: "Please enter user's name" }]}
          >
            <Input placeholder="User full name" disabled={isFormReadOnly} />
          </StyledFormItem>

          <StyledFormItem
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter user email address' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input placeholder="User email address" disabled={isFormReadOnly} />
          </StyledFormItem>

          <StyledFormItem
            name="phoneNumber"
            label="Phone Number"
            rules={[
              { required: true, message: 'Please enter phone number' },
              {
                pattern: /^[0-9]{10}$/,
                message: 'Phone number must be exactly 10 digits',
              },
            ]}
          >
            <Input
              addonBefore="+91"
              maxLength={10}
              placeholder="User phone number"
              disabled={isFormReadOnly}
            />
          </StyledFormItem>

          <StyledFormItem
            name="userRole"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <StyledSelect
              placeholder="Select a role"
              disabled={
                initialValues?.userRole === UserRole.FacilityOwner ||
                initialValues?.userRole === UserRole.EventOrganizer
              }
            >
              {Object.entries(UserRole)
                .filter(([key, value]) => typeof value === 'number')
                .map(([role, id]) => {
                  const isSelectable = id === 1 || id === 6;
                  const isCurrent = initialValues?.userRole === id;

                  if (isSelectable || isCurrent) {
                    return (
                      <StyledOption
                        key={id}
                        value={id}
                        $isSelectable={isSelectable}
                      >
                        {role.replace(/([A-Z])/g, ' $1').trim()}
                      </StyledOption>
                    );
                  }
                  return null;
                })}
            </StyledSelect>
          </StyledFormItem>

          <StyledFormItem
            name="entityStatus"
            label="Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <StyledSelect placeholder="Select status" disabled={isFormReadOnly}>
              {Object.entries(status).map(([status, id]) => (
                <Option key={id} value={id}>
                  {status}
                </Option>
              ))}
            </StyledSelect>
          </StyledFormItem>
        </Form>
      </StyledCol>
    </Drawer>
  );
};

const StyledSelect = styled(Select)`
  width: 100%;
`;

const StyledOption = styled(Option)<StyledOptionProps>`
  display: ${({ $isSelectable }) => ($isSelectable ? 'block' : 'none')};
`;

export default UserForm;
